import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { usersApi } from '../api/client';
import type { PublicProfile as PublicProfileType } from '../api/client';
import './PublicProfile.css';

const PublicProfile: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const [profile, setProfile] = useState<PublicProfileType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!username) return;

    const fetchProfile = async () => {
      try {
        setLoading(true);
        const response = await usersApi.getPublicProfile(username);
        setProfile(response.data);
      } catch (err: any) {
        console.error('Error fetching public profile:', err);
        if (err.response?.status === 404) {
          setError('User not found');
        } else {
          setError('Failed to load profile');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [username]);

  if (loading) {
    return (
      <div className="public-profile">
        <div className="loading">Loading profile...</div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="public-profile">
        <div className="error-container">
          <h1>Profile Not Found</h1>
          <p>{error || 'The requested profile could not be found.'}</p>
          <Link to="/" className="home-link">← Back to Home</Link>
        </div>
      </div>
    );
  }

  const { user, blogs, blogPosts, stats } = profile;

  return (
    <div className="public-profile">
      {/* Header */}
      <header className="profile-header">
        <div className="profile-info">
          <div className="avatar-container">
            {user.avatar ? (
              <img src={user.avatar} alt={user.displayName} className="avatar" />
            ) : (
              <div className="avatar-placeholder">
                {user.displayName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="user-details">
            <h1 className="display-name">{user.displayName}</h1>
            <p className="username">@{user.username}</p>
            {user.profile.bio && (
              <p className="bio">{user.profile.bio}</p>
            )}
            {user.profile.location && (
              <div className="location">
                📍 {user.profile.location}
              </div>
            )}
            <div className="join-date">
              Joined {new Date(user.createdAt).toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric'
              })}
            </div>
          </div>
        </div>
        
        <div className="profile-stats">
          <div className="stat">
            <span className="stat-number">{stats.totalBlogs}</span>
            <span className="stat-label">Blogs</span>
          </div>
          <div className="stat">
            <span className="stat-number">{stats.publishedPosts}</span>
            <span className="stat-label">Posts</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="profile-content">
        {/* Blogs Section */}
        {blogs.length > 0 && (
          <section className="blogs-section">
            <h2>Blogs</h2>
            <div className="blogs-grid">
              {blogs.map(blog => {
                const blogPostCount = blogPosts.filter(post => post.blogId === blog.id).length;
                return (
                  <div key={blog.id} className="blog-card">
                    <div className="blog-header">
                      <h3>{blog.title}</h3>
                      <div className="blog-meta">
                        <span className="post-count">{blogPostCount} posts</span>
                        <span className="blog-theme">{blog.theme}</span>
                      </div>
                    </div>
                    {blog.description && (
                      <p className="blog-description">{blog.description}</p>
                    )}
                    <div className="blog-tags">
                      {blog.metadata.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="tag">{tag}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Recent Posts Section */}
        {blogPosts.length > 0 && (
          <section className="posts-section">
            <h2>Recent Posts</h2>
            <div className="posts-list">
              {blogPosts.slice(0, 10).map(post => (
                <article key={post.id} className="post-card">
                  <div className="post-header">
                    <h3 className="post-title">{post.title}</h3>
                    <div className="post-meta">
                      <span className="blog-name">{post.blog?.title}</span>
                      <span className="separator">•</span>
                      <span className="post-date">
                        {new Date(post.publishedAt || post.createdAt).toLocaleDateString()}
                      </span>
                      <span className="separator">•</span>
                      <span className="reading-time">{post.metadata.readingTime} min read</span>
                    </div>
                  </div>
                  
                  {post.excerpt && (
                    <p className="post-excerpt">{post.excerpt}</p>
                  )}
                  
                  <div className="post-footer">
                    <div className="post-tags">
                      {post.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="tag">{tag}</span>
                      ))}
                    </div>
                    
                    <div className="post-stats">
                      <span className="word-count">{post.metadata.wordCount} words</span>
                      {post.settings.isFeatured && (
                        <span className="featured-badge">Featured</span>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Empty State */}
        {blogs.length === 0 && blogPosts.length === 0 && (
          <div className="empty-state">
            <h3>No content yet</h3>
            <p>{user.displayName} hasn't published any blogs or posts yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicProfile;